import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronDown,
  ChevronRight,
  Check,
  AlertTriangle,
  Shield,
  Calendar,
  Users,
  Building,
  FileText,
  Scale,
  Clock,
  AlertCircle,
  BookOpen,
  ArrowLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { POSTHOG_EVENTS } from "../constants";
import { usePostHog } from "posthog-js/react";

const NIS2Guide = () => {
  const [activeSection, setActiveSection] = useState("overview");
  const [expandedSectors, setExpandedSectors] = useState({});
  const posthog = usePostHog();

  const toggleSector = (sector: string) => {
    setExpandedSectors((prev) => ({ ...prev, [sector]: !prev[sector] }));
  };

  const sectors = [
    {
      name: "Energie",
      critical: true,
      subsectors: [
        "Electricitate (furnizori, distribuitori, producători)",
        "Încălzire centralizată",
        "Petrol (rafinării, conducte, depozitare)",
        "Gaze naturale (furnizare, distribuție, transport)",
        "Hidrogen",
      ],
      examples:
        "Enel, E.ON, Hidroelectrica, Transelectrica, OMV Petrom, Transgaz",
    },
    {
      name: "Transport",
      critical: true,
      subsectors: [
        "Transport aerian (transportatori, aeroporturi, ATC)",
        "Transport feroviar (CFR, operatori privați)",
        "Transport pe apă (porturi, navlositori)",
        "Transport rutier (CNAIR, sisteme inteligente)",
        "Transport public (STB, Metrorex)",
      ],
      examples: "TAROM, ROMATSA, CFR, Aeroporturi București",
    },
    {
      name: "Sectorul Bancar",
      critical: true,
      subsectors: ["Instituții de credit"],
      examples: "Banca Transilvania, BCR, BRD, ING Bank, Raiffeisen",
    },
    {
      name: "Infrastructură Digitală",
      critical: true,
      subsectors: [
        "Furnizori cloud computing",
        "Centre de date",
        "Furnizori DNS și IXP",
        "Registre TLD",
        "Telecomunicații",
      ],
      examples: "AWS, Azure, Orange, Vodafone, Digi, RoTLD",
    },
    {
      name: "Sănătate",
      critical: true,
      subsectors: [
        "Furnizori servicii medicale",
        "Producători farmaceutici",
        "Distribuitori medicamente",
        "Dispozitive medicale",
      ],
      examples: "Spitale județene, Regina Maria, MedLife, Catena, Dr. Max",
    },
    {
      name: "Apă",
      critical: true,
      subsectors: ["Furnizori apă potabilă", "Tratare ape uzate"],
      examples: "Apa Nova, Aquatim, operatori locali",
    },
    {
      name: "Administrație Publică",
      critical: true,
      subsectors: ["Entități centrale"],
      examples: "Ministere, ANAF, CNAS",
    },
    {
      name: "Servicii Poștale",
      critical: false,
      subsectors: ["Poștă și curierat"],
      examples: "Poșta Română, Fan Courier, DHL",
    },
    {
      name: "Producție",
      critical: false,
      subsectors: [
        "Alimente",
        "Substanțe chimice",
        "Echipamente electronice",
        "Autovehicule",
      ],
      examples: "Dacia, Ford Craiova, Smithfield, Azomureș",
    },
    {
      name: "Furnizori Digitali",
      critical: false,
      subsectors: ["Piețe online", "Motoare de căutare", "Rețele sociale"],
      examples: "eMAG Marketplace, Uber, Glovo, Meta, Google",
    },
  ];

  const complianceSteps = [
    {
      id: "step1",
      title: "Auto-identificare și Notificare",
      deadline: "30 zile de la încadrare",
      description:
        "Determinați dacă sunteți entitate vizată și notificați DNSC prin PlatformaNIS2.ro",
      icon: <FileText className="w-5 h-5" />,
      details: [
        "Folosiți instrumentul NIS2@RO (Excel)",
        "Completați datele de identificare (CUI, contact)",
        "Includeți intervalele IP publice",
        "Transmiteți prin PlatformaNIS2.ro sau email la evidenta@dnsc.ro",
      ],
    },
    {
      id: "step2",
      title: "Evaluare Nivel de Risc",
      deadline: "După notificare",
      description:
        "Calculați scorul de risc (0-1500 puncte) conform Ordinului DNSC nr. 2/2025",
      icon: <AlertTriangle className="w-5 h-5" />,
      details: [
        "Nivel de bază: 0-99 puncte",
        "Nivel important: 100-199 puncte",
        "Nivel esențial: 200-1500 puncte",
        "Scorul determină complexitatea măsurilor necesare",
      ],
    },
    {
      id: "step3",
      title: "Autoevaluare Maturitate",
      deadline: "60 zile de la evaluarea de risc",
      description:
        "Realizați analiza GAP între măsurile existente și cerințele legale",
      icon: <Scale className="w-5 h-5" />,
      details: [
        "Comparați măsurile existente cu Art. 13 OUG 155/2024",
        "Documentați deficiențele identificate",
        "Pentru entități esențiale: elaborați plan de măsuri în 30 zile",
        "Folosiți Platforma NIS2@RO pentru raportare",
      ],
    },
    {
      id: "step4",
      title: "Implementare Măsuri Tehnice",
      deadline: "Conform planului de măsuri",
      description: "Implementați cele 10 categorii de măsuri obligatorii",
      icon: <Shield className="w-5 h-5" />,
      details: [
        "Politici și analiză de risc",
        "Gestionare incidente și raportare (24h/72h/1 lună)",
        "Continuitate și recuperare",
        "Securitate lanț aprovizionare",
        "Securitate dezvoltare și mentenanță",
        "Formare personal și conducere",
        "Criptografie și criptare",
        "Control acces și PAM",
        "Autentificare multi-factor",
        "Comunicații securizate",
      ],
    },
    {
      id: "step5",
      title: "Verificări Continue",
      deadline: "Anual",
      description: "Mențineți conformitatea prin evaluări regulate",
      icon: <Calendar className="w-5 h-5" />,
      details: [
        "Autoevaluare anuală obligatorie",
        "Audit extern (frecvență conform nivel risc)",
        "Actualizare măsuri conform evoluției amenințărilor",
        "Raportare incidente semnificative",
      ],
    },
  ];

  const sanctions = [
    {
      type: "Entități Esențiale",
      violations: "Nerespectare măsuri securitate sau raportare incidente",
      fine: "10.000.000 EUR sau 2% din cifra de afaceri globală",
      color: "destructive",
    },
    {
      type: "Entități Importante",
      violations: "Nerespectare măsuri securitate sau raportare incidente",
      fine: "7.000.000 EUR sau 1.4% din cifra de afaceri globală",
      color: "warning",
    },
    {
      type: "Responsabilitate Personală",
      violations: "Neimplementare măsuri dispuse de DNSC",
      fine: "Interdicție temporară funcție conducere",
      color: "secondary",
    },
  ];

  const technicalSolutions = [
    {
      name: "SIEM",
      description: "Monitorizare și corelarea evenimentelor de securitate",
    },
    {
      name: "EDR/XDR",
      description: "Detectare și răspuns amenințări endpoint",
    },
    { name: "PAM", description: "Management acces privilegiat" },
    {
      name: "Backup & Recovery",
      description: "Copii de siguranță și recuperare",
    },
    {
      name: "Vulnerability Scanner",
      description: "Identificare vulnerabilități",
    },
    { name: "MFA", description: "Autentificare multi-factor" },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="gradient-hero">
        <div className="max-w-6xl mx-auto px-4 py-8 sm:py-12">
          <div className="flex items-center gap-4 mb-6">
            <Link to="/">
              <Button
                variant="outline"
                size="sm"
                className="bg-white/10 backdrop-blur border-white/20 text-white hover:bg-white/20"
              >
                <ArrowLeft className="w-4 h-4" />
                Înapoi
              </Button>
            </Link>
          </div>
          <div className="flex items-center gap-3 mb-4 text-white">
            <Shield className="w-8 h-8 sm:w-10 sm:h-10" />
            <h1 className="text-2xl sm:text-4xl font-bold">
              Ghid NIS2 România
            </h1>
          </div>
          <p className="text-white/80 text-sm sm:text-lg">
            Conformitate cu OUG 155/2024 - Directiva NIS2
          </p>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="sticky top-0 z-10 bg-background shadow-card border-b">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex overflow-x-auto gap-2 py-3 no-scrollbar">
            {[
              {
                id: "overview",
                label: "Prezentare",
                icon: <BookOpen className="w-4 h-4" />,
              },
              {
                id: "sectors",
                label: "Sectoare",
                icon: <Building className="w-4 h-4" />,
              },
              {
                id: "steps",
                label: "Pași",
                icon: <Check className="w-4 h-4" />,
              },
              {
                id: "measures",
                label: "Măsuri",
                icon: <Shield className="w-4 h-4" />,
              },
              {
                id: "sanctions",
                label: "Sancțiuni",
                icon: <AlertCircle className="w-4 h-4" />,
              },
            ].map((tab) => (
              <Button
                key={tab.id}
                onClick={() => setActiveSection(tab.id)}
                variant={activeSection === tab.id ? "default" : "outline"}
                size="sm"
                className="flex items-center gap-2 whitespace-nowrap"
              >
                {tab.icon}
                <span className="text-sm font-medium">{tab.label}</span>
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Overview Section */}
        {activeSection === "overview" && (
          <div className="space-y-6">
            <Card className="shadow-card">
              <CardHeader>
                <CardTitle className="text-2xl">Ce este NIS2?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground">
                <p>
                  Directiva NIS2 (Directiva UE 2022/2555) reprezintă o
                  actualizare majoră a cadrului de securitate cibernetică
                  european, transpusă în România prin OUG 155/2024.
                </p>
                <div className="bg-primary/10 border-l-4 border-primary p-4 rounded">
                  <p className="font-semibold text-primary mb-2">
                    Termen limită notificare: 22 septembrie 2025
                  </p>
                  <p className="text-primary/80">
                    20 august 2025 - Data intrării în vigoare
                  </p>
                </div>
              </CardContent>
            </Card>

            <div className="grid md:grid-cols-2 gap-6">
              <Card className="shadow-card">
                <CardHeader>
                  <CardTitle className="text-lg">Cine este vizat?</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                      <span>
                        Întreprinderi mari și mijlocii din sectoare critice
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                      <span>
                        Furnizori de servicii digitale și telecomunicații
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                      <span>Administrație publică centrală</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                      <span>Entități unice sau critice regional</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="shadow-card">
                <CardHeader>
                  <CardTitle className="text-lg">
                    Autorități competente
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="bg-muted p-3 rounded-lg">
                    <p className="font-semibold">DNSC</p>
                    <p className="text-sm text-muted-foreground">
                      Directoratul Național de Securitate Cibernetică -
                      Autoritate principală
                    </p>
                  </div>
                  <div className="bg-muted p-3 rounded-lg">
                    <p className="font-semibold">ANCOM</p>
                    <p className="text-sm text-muted-foreground">
                      Pentru comunicații electronice și servicii digitale
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Sectors Section */}
        {activeSection === "sectors" && (
          <div className="space-y-4">
            <Card className="shadow-card mb-6">
              <CardHeader>
                <CardTitle className="text-2xl">
                  Sectoare vizate de NIS2
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Identificați dacă organizația dvs. face parte din unul dintre
                  sectoarele de mai jos. Entitățile mari și mijlocii sunt în
                  general vizate, cu excepții pentru anumite subsectoare.
                </p>
              </CardContent>
            </Card>

            {sectors.map((sector, index) => (
              <Card key={index} className="shadow-card overflow-hidden">
                <button
                  onClick={() => toggleSector(sector.name)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-2 h-8 rounded ${sector.critical ? "bg-destructive" : "bg-warning"}`}
                    />
                    <div className="text-left">
                      <h3 className="font-bold">{sector.name}</h3>
                      <Badge
                        variant={sector.critical ? "destructive" : "secondary"}
                        className="text-xs"
                      >
                        {sector.critical
                          ? "Sector critic"
                          : "Alt sector important"}
                      </Badge>
                    </div>
                  </div>
                  {expandedSectors[sector.name] ? (
                    <ChevronDown className="w-5 h-5 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  )}
                </button>

                {expandedSectors[sector.name] && (
                  <div className="px-6 pb-4 border-t bg-muted/30">
                    <div className="pt-4 space-y-3">
                      <div>
                        <p className="font-semibold mb-2">Subsectoare:</p>
                        <ul className="space-y-1">
                          {sector.subsectors.map((sub, i) => (
                            <li
                              key={i}
                              className="text-sm text-muted-foreground flex items-start gap-2"
                            >
                              <span className="text-primary mt-0.5">•</span>
                              {sub}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <p className="font-semibold mb-1">Exemple:</p>
                        <p className="text-sm text-muted-foreground italic">
                          {sector.examples}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}

        {/* Steps Section */}
        {activeSection === "steps" && (
          <div className="space-y-6">
            <Card className="shadow-card">
              <CardHeader>
                <CardTitle className="text-2xl">
                  Pași pentru conformare NIS2
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Urmați acești 5 pași obligatorii pentru a vă conforma cu
                  cerințele OUG 155/2024.
                </p>
              </CardContent>
            </Card>

            <div className="space-y-4">
              {complianceSteps.map((step, index) => (
                <Card key={step.id} className="shadow-card">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-primary/10 rounded-lg">
                              {step.icon}
                            </div>
                            <div>
                              <h3 className="font-bold">
                                Pasul {index + 1}: {step.title}
                              </h3>
                              <div className="flex items-center gap-2 mt-1">
                                <Clock className="w-4 h-4 text-warning" />
                                <span className="text-sm text-warning font-medium">
                                  {step.deadline}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <p className="text-muted-foreground mb-3">
                          {step.description}
                        </p>

                        <div className="bg-muted/50 rounded-lg p-4">
                          <p className="font-semibold mb-2">
                            Acțiuni necesare:
                          </p>
                          <ul className="space-y-1">
                            {step.details.map((detail, i) => (
                              <li
                                key={i}
                                className="text-sm text-muted-foreground flex items-start gap-2"
                              >
                                <ChevronRight className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                                {detail}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Measures Section */}
        {activeSection === "measures" && (
          <div className="space-y-6">
            <Card className="shadow-card">
              <CardHeader>
                <CardTitle className="text-2xl">
                  Măsuri tehnice și organizatorice
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Articolul 13 din OUG 155/2024 impune 10 categorii de măsuri
                  obligatorii. Complexitatea implementării depinde de nivelul
                  dvs. de risc.
                </p>
              </CardContent>
            </Card>

            <div className="grid md:grid-cols-2 gap-6">
              <Card className="shadow-card">
                <CardHeader>
                  <CardTitle className="text-lg">Măsuri obligatorii</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {[
                      "Politici și analiză de risc (all-hazards)",
                      "Plan de răspuns la incidente",
                      "Continuitate și recuperare dezastre",
                      "Securitate lanț aprovizionare",
                      "Securitate dezvoltare software",
                      "Formare personal și conducere",
                      "Politici de criptografie",
                      "Control acces și PAM",
                      "Autentificare multi-factor",
                      "Comunicații securizate",
                    ].map((measure, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Shield className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                        <span className="text-muted-foreground">{measure}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <Card className="shadow-card">
                <CardHeader>
                  <CardTitle className="text-lg">
                    Soluții tehnice recomandate
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {technicalSolutions.map((solution, i) => (
                    <div key={i} className="bg-muted/50 rounded-lg p-3">
                      <p className="font-semibold">{solution.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {solution.description}
                      </p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>

            <div className="bg-warning/10 border-l-4 border-warning p-6 rounded-xl">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-warning flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-warning-foreground mb-2">
                    Raportare incidente
                  </h4>
                  <ul className="space-y-2 text-warning-foreground">
                    <li>
                      • <strong>24 ore:</strong> Avertizare timpurie pentru
                      incidente semnificative
                    </li>
                    <li>
                      • <strong>72 ore:</strong> Notificare detaliată cu
                      evaluare impact
                    </li>
                    <li>
                      • <strong>1 lună:</strong> Raport final cu măsuri
                      remediere
                    </li>
                  </ul>
                  <p className="mt-3 text-sm">
                    Folosiți platforma PNRISC pentru raportare
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Sanctions Section */}
        {activeSection === "sanctions" && (
          <div className="space-y-6">
            <Card className="shadow-card">
              <CardHeader>
                <CardTitle className="text-2xl">
                  Sancțiuni pentru neconformitate
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  OUG 155/2024 prevede sancțiuni severe pentru nerespectarea
                  obligațiilor. Amenzile se calculează ca valoare maximă între
                  suma fixă și procentul din cifra de afaceri.
                </p>
              </CardContent>
            </Card>

            <div className="space-y-4">
              {sanctions.map((sanction, i) => (
                <Card
                  key={i}
                  className="shadow-card border-l-4 border-destructive"
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-bold mb-2">{sanction.type}</h3>
                        <p className="text-muted-foreground mb-3">
                          {sanction.violations}
                        </p>
                        <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-destructive/10">
                          <AlertCircle className="w-5 h-5 text-destructive" />
                          <span className="font-bold text-destructive">
                            {sanction.fine}
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="bg-secondary/10 border-l-4 border-secondary p-6 rounded-xl">
              <div className="flex items-start gap-3">
                <Users className="w-6 h-6 text-secondary flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-secondary-foreground mb-2">
                    Responsabilitate conducerii
                  </h4>
                  <p className="text-secondary-foreground">
                    Membrii organelor de conducere au responsabilitate personală
                    pentru conformare. Nerespectarea poate duce la interdicția
                    temporară de a exercita funcții de conducere.
                  </p>
                  <ul className="mt-3 space-y-1 text-secondary-foreground">
                    <li>• Aprobare formală măsuri securitate</li>
                    <li>• Supraveghere activă implementare</li>
                    <li>• Participare cursuri formare acreditate</li>
                    <li>• Alocare resurse adecvate</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Call to Action */}
        <div className="mt-12 text-center">
          <Card className="shadow-card bg-primary/5">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold mb-4">
                Gata să începi evaluarea?
              </h3>
              <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                Folosește platforma noastră pentru a determina dacă compania ta
                trebuie să se conformeze cu cerințele NIS2 și generează automat
                formularul de notificare către DNSC.
              </p>
              <Link
                to="/identificare-raportare"
                onClick={() => {
                  posthog.capture(POSTHOG_EVENTS.START_EVALUATION_DESPRE_NIS2);
                }}
              >
                <Button
                  size="lg"
                  variant="hero"
                  className="text-lg px-8 py-4 h-auto"
                >
                  <Shield className="w-5 h-5" />
                  Începe Evaluarea NIS2
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default NIS2Guide;
